// frontend/src/components/Samples/LineOperationsModal.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiFilter, FiEdit2, FiRefreshCw, FiClock } from 'react-icons/fi';
import { lineOperationsAPI, usersAPI, attachmentsAPI } from '../../api/client';
import CommentsButton from '../Common/Comments/CommentsButton';
import { buildTaskCommentLabel } from '../Common/Comments/commentLabels';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import LineOperationDetailsModal from './LineOperationDetailsModal';
import AuditLogPanel from '../Common/AuditLog/AuditLogPanel';
import './LineOperationsModal.css';
import { TASK_STATUS_META } from '../../constants/statusMeta';

// Extend global TASK_STATUS_META with LOM-specific extra statuses
const STATUS_META = {
  ...Object.fromEntries(
    Object.entries(TASK_STATUS_META).map(([k, v]) => [k, { label: v.label, cls: v.cls }])
  ),
  // Extra statuses only used in line operations modal
  PENDING: { label: 'Pendente',  cls: 'status--new' },
  DONE:    { label: 'Concluído', cls: 'status--completed' },
  N_A:     { label: 'N/A',       cls: 'status--na' },
  BLOCKED: { label: 'Bloqueado', cls: 'status--cancelled' },
};

const FILTERS = {
  ALL:          'ALL',
  SELECTED:     'SELECTED',
  NOT_SELECTED: 'NOT_SELECTED',   // unchecked operations (replaces PENDING)
  COMPLETED:    'COMPLETED',
};

const parseOptions = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw.length ? raw : null;

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch {
    return null;
  }
};

const getOperationUI = (op, t) => {
  const options = parseOptions(op.field_options || op.bool_field_label);

  if (options?.length) {
    return {
      kind: 'choice',
      label: op.bool_field_label || t('areas.result', 'Resultado'),
      options,
    };
  }

  if (op.text_field_label) {
    return {
      kind: 'text',
      label: op.text_field_label || t('areas.result', 'Resultado'),
    };
  }

  return {
    kind: 'notes_only',
    label: t('areas.observation', 'Observação'),
  };
};

const getEmptyForm = () => ({
  status: 'NEW',
  choice_value: '',
  text_value: '',
  operation_instructions: '',
  completion_notes: '',
  pendingImages: [],

  planned_start_date: '',
  planned_end_date: '',
  real_start_date: '',
  real_end_date: '',
  assigned_user_id: '',
});

const mapSavedToForm = (op, savedItem, t) => {
  const ui = getOperationUI(op, t);

  const base = {
    ...getEmptyForm(),
    status: savedItem?.status || 'NEW',
    operation_instructions: savedItem?.operation_instructions || '',
    completion_notes: savedItem?.completion_notes || '',
    pendingImages: [],

    planned_start_date: savedItem?.planned_start_date?.slice(0, 10) || '',
    planned_end_date:   savedItem?.planned_end_date  ?.slice(0, 10) || '',
    real_start_date:    savedItem?.real_start_date   ?.slice(0, 10) || '',
    real_end_date:      savedItem?.real_end_date     ?.slice(0, 10) || '',
    assigned_user_id:   savedItem?.assigned_user_id ? String(savedItem.assigned_user_id) : '',

    // Stored so LineOperationDetailsModal can gate FileUpload without needing isSaved
    line_operations_id: savedItem?.line_operations_id || null,
    saved_public_id:    savedItem?.public_id           || null,
  };

  if (!savedItem) return base;

  if (ui.kind === 'choice') {
    return {
      ...base,
      choice_value: savedItem.result_value || '',
    };
  }

  if (ui.kind === 'text') {
    return {
      ...base,
      text_value: savedItem.result_value || '',
    };
  }

  return base;
};

const getResultValueForSave = (op, form, t) => {
  const ui = getOperationUI(op, t);

  if (ui.kind === 'choice') return form.choice_value || null;
  if (ui.kind === 'text') return form.text_value || null;
  return null;
};

const getSummary = (op, form, isSaved, t) => {
  const ui = getOperationUI(op, t);
  const parts = [];

  if (ui.kind === 'choice') {
    parts.push(form.choice_value ? `${t('areas.result', 'Resultado')}: ${form.choice_value}` : 'Por preencher');
  } else if (ui.kind === 'text') {
    parts.push(form.text_value ? 'Texto preenchido' : 'Texto por preencher');
  } else {
    parts.push(form.operation_instructions ? 'Com observação' : 'Sem observação');
  }

  if (form.operation_instructions && ui.kind !== 'notes_only') {
    parts.push('Observação preenchida');
  }

  if (form.assigned_user_id) {
    parts.push('Responsável definido');
  }

  if (form.planned_end_date) {
    parts.push(`Fim: ${form.planned_end_date}`);
  }

  if (isSaved && form.status) {
    const meta = STATUS_META[form.status] || STATUS_META.PENDING;
    parts.push(meta.label);
  }

  return parts.slice(0, 2).join(' · ');
};

const LineOperationsModal = ({
  orderPublicId,
  orderId,           // sample_order_header.sample_order_id (internal) — task comments are stored on the order
  matrixLineNo,
  lineLabel,
  lineArticle,       // { enteredProductCode, enteredProductColor, erpProductCode, erpProductColorCode }
  orderDate,
  orderCreatedAt,    // sample_order_header.created_at — used as default planned_start_date
  defaultFilter = FILTERS.ALL,  // caller can pre-select a filter tab
  onClose,
}) => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [allOps, setAllOps] = useState([]);
  const [savedOps, setSavedOps] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [opForms, setOpForms] = useState({});
  const [assignableUsers, setAssignableUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [startingCycle, setStartingCycle] = useState(false);

  const [currentCycleNo, setCurrentCycleNo] = useState(null);
  const [availableCycles, setAvailableCycles] = useState([]);

  const [activeFilter, setActiveFilter] = useState(defaultFilter);
  const [search, setSearch] = useState('');
  const [selectedArea, setSelectedArea] = useState('ALL');
  const [editingKey, setEditingKey] = useState(null);
  // { entityType, id, label } | null — used for both the order history (header)
  // and a single task's history (row clock icon)
  const [auditLogOp, setAuditLogOp] = useState(null);

  const taskCommentLabel = (op) => buildTaskCommentLabel({
    areaCode:       op.area_code,
    operationLabel: op.description || op.operation_label,
    matrixLineNo,
    productCode:    lineArticle?.enteredProductCode || lineArticle?.erpProductCode || lineLabel,
    productColor:   lineArticle?.enteredProductColor,
  });


  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  
    useEffect(() => {
      console.log('[LineOperationsModal.jsx] mounted');
      return () => console.log('[LineOperationsModal.jsx] unmounted');
    }, []);  
  


  useEffect(() => {
  let cancelled = false;

  const load = async () => {
    if (orderPublicId == null || matrixLineNo == null) {
      console.warn('[LineOperationsModal] missing orderPublicId or matrixLineNo — skipping load');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);

      const [opsRes, savedRes, usersRes, cyclesRes] = await Promise.all([
        lineOperationsAPI.getAvailableOperations(),
        lineOperationsAPI.getForArticle(orderPublicId, matrixLineNo, currentCycleNo),
        usersAPI.getAll(),
        lineOperationsAPI.getCycles(orderPublicId, matrixLineNo).catch(() => ({ data: { success: true, data: [] } })),
      ]);

      if (cancelled) return;

      const ops   = opsRes?.data?.success  ? opsRes.data.data  : [];
      const saved = savedRes?.data?.success ? savedRes.data.data : [];
      const users = usersRes?.data?.success ? (usersRes.data.users || []) : [];

      // Active ops from getAvailableOperations + inactive ops that have saved records
      const activeIds = new Set(ops.map(o => String(o.area_operations_id)));
      const savedInactiveOps = saved
        .filter(s => !activeIds.has(String(s.operation_id)))
        .map(s => ({
          area_operations_id:   s.operation_id,
          area_id:              s.area_id,
          area_operations_code: s.operation_code,
          description:          s.operation_label,
          bool_field_label:     s.bool_field_label,
          text_field_label:     s.text_field_label,
          field_options:        s.field_options,
          area_code:            s.area_code,
          sequence_no:          s.op_sequence  ?? 9999,
          area_sequence:        s.area_sequence ?? 9999,
          _inactive:            true,
        }));
      const mergedOps = [...ops, ...savedInactiveOps];
      const cycles  = cyclesRes?.data?.success ? cyclesRes.data.data : [];
      const maxCycle = cycles.length > 0 ? Math.max(...cycles.map(cy => cy.cycle_no)) : 1;
      setAllOps(mergedOps);
      setSavedOps(saved);
      setAssignableUsers(users);
      setAvailableCycles(cycles);
      if (currentCycleNo === null) setCurrentCycleNo(maxCycle);

      const savedMapLocal = {};
      saved.forEach((s) => {
        savedMapLocal[String(s.operation_id)] = s;
      });

      const initialChecked = new Set(saved.map((s) => String(s.operation_id)));
      const initialForms = {};

      mergedOps.forEach((op) => {
        const key = String(op.area_operations_id);
        initialForms[key] = mapSavedToForm(op, savedMapLocal[key], t);
      });

      setChecked(initialChecked);
      setOpForms(initialForms);
    } catch (err) {
      console.error(err);
      if (!cancelled) {
        toast_error(err.response?.data?.error || t('messages.error_load', 'Erro ao carregar'));
      }
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  load();

  return () => {
    cancelled = true;
  };
}, [orderPublicId, matrixLineNo, currentCycleNo]);


  const savedMap = useMemo(() => {
    const map = {};
    savedOps.forEach((s) => {
      map[String(s.operation_id)] = s;
    });
    return map;
  }, [savedOps]);

  const areaOptions = useMemo(() => {
    const seen = new Map();

    allOps.forEach((op) => {
      if (!seen.has(op.area_code)) {
        seen.set(op.area_code, {
          area_code: op.area_code,
          area_sequence_no: op.area_sequence ?? 0,
        });
      }
    });

return [...seen.values()].sort((a, b) => {
  if ((a.area_sequence_no ?? 0) !== (b.area_sequence_no ?? 0)) {
    return (a.area_sequence_no ?? 0) - (b.area_sequence_no ?? 0);
  }
  return String(a.area_code).localeCompare(String(b.area_code));
});
  }, [allOps]);

  const matchesFilter = (op) => {
    const key = String(op.area_operations_id);
    const isSelected = checked.has(key);
    const form = opForms[key] || getEmptyForm();
    const status = form.status || savedMap[key]?.status || 'PENDING';

    if (activeFilter === FILTERS.SELECTED)     return isSelected;
    if (activeFilter === FILTERS.NOT_SELECTED) return !isSelected;
    if (activeFilter === FILTERS.COMPLETED)    return status === 'COMPLETED' || status === 'DONE';
    return true;
  };

  const filteredOps = useMemo(() => {
    return [...allOps]
      .filter((op) => {
        if (selectedArea !== 'ALL' && op.area_code !== selectedArea) return false;

        if (search.trim()) {
          const q = search.toLowerCase();
          const haystack = [
            op.area_code,
            op.description,
            op.operation_label,
            op.text_field_label,
            op.bool_field_label,
            op.area_operations_code,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          if (!haystack.includes(q)) return false;
        }

        return matchesFilter(op);
      })
      .sort((a, b) => {
        const areaDiff = (a.area_sequence ?? 0) - (b.area_sequence_no ?? 0);
        if (areaDiff !== 0) return areaDiff;

        const opDiff = (a.sequence_no ?? 0) - (b.sequence_no ?? 0);
        if (opDiff !== 0) return opDiff;

        return String(a.description || a.operation_label || '').localeCompare(
          String(b.description || b.operation_label || '')
        );
      });
  }, [allOps, selectedArea, search, activeFilter, checked, opForms, savedMap, t]);

  // Separate active vs inactive for accurate counts
  const activeOps      = allOps.filter(op => !op._inactive);
  const activeOpIdsSet = new Set(activeOps.map(op => String(op.area_operations_id)));
  const selectedActive = [...checked].filter(id => activeOpIdsSet.has(id)).length;
  const filterCounts = {
    [FILTERS.ALL]:          allOps.length,
    [FILTERS.SELECTED]:     selectedActive,
    [FILTERS.NOT_SELECTED]: activeOps.filter(op => !checked.has(String(op.area_operations_id))).length,
    [FILTERS.COMPLETED]:    allOps.filter((op) => {
      const key = String(op.area_operations_id);
      const form = opForms[key] || getEmptyForm();
      const status = form.status || savedMap[key]?.status || 'PENDING';
      return status === 'COMPLETED' || status === 'DONE';
    }).length,
  };

const toggleChecked = (rawKey) => {
  const key = String(rawKey);
  const isCurrentlyChecked = checked.has(key);

  setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  // When checking (not unchecking), open details — prefill happens inside openDetails
  if (!isCurrentlyChecked) {
    openDetails(key);
  }
};

  const openDetails = (rawKey) => {
    const key = String(rawKey);
    const currentForm = opForms[key] || getEmptyForm();
    const isNew = !currentForm.saved_public_id; // no DB row yet

    // Always apply defaults when there is no saved data:
    // — planned_start_date from the order creation date
    // — assigned_user_id from the area_operation default
    const defaultStart = (orderCreatedAt || orderDate || '')?.slice(0, 10);
    const updates = {};

    if (!currentForm.planned_start_date && defaultStart) {
      updates.planned_start_date = defaultStart;
    }
    if (isNew && !currentForm.assigned_user_id) {
      const op = allOps.find(o => String(o.area_operations_id) === key);
      if (op?.default_assigned_user_id) {
        updates.assigned_user_id = String(op.default_assigned_user_id);
      }
    }

    if (Object.keys(updates).length > 0) {
      setOpForms(prev => ({ ...prev, [key]: { ...currentForm, ...updates } }));
    }

    setEditingKey(key);
  };

  const closeDetails = () => {
    setEditingKey(null);
  };


const handleDetailsSave = async (rawKey, updatedForm) => {
  const key = String(rawKey);
  const savedItem = savedMap[key];

  try {
    if (savedItem?.public_id) {
      // Existing row: update directly
      await lineOperationsAPI.update(savedItem.public_id, {
        status: updatedForm.status || null,
        planned_start_date: updatedForm.planned_start_date || null,
        planned_end_date: updatedForm.planned_end_date || null,
        real_start_date: updatedForm.real_start_date || null,
        real_end_date: updatedForm.real_end_date || null,
        operation_instructions: updatedForm.operation_instructions || null,
        completion_notes: updatedForm.completion_notes || null,
        result_value: updatedForm.choice_value || updatedForm.text_value || null,
        assigned_user_id: updatedForm.assigned_user_id
          ? Number(updatedForm.assigned_user_id)
          : null,
      });
    } else {
      // New row: save through the bulk article save endpoint
      const nextForms = {
        ...opForms,
        [key]: updatedForm,
      };

      const nextChecked = new Set(checked);
      nextChecked.add(key);

      const operations = allOps.map((op) => {
        const opKey = String(op.area_operations_id);
        const form = nextForms[opKey] || getEmptyForm();

        return {
          operation_id: op.area_operations_id,
          checked: nextChecked.has(opKey),
          lead_time_days: op.default_lead_time_days ?? 0,
          result_value: getResultValueForSave(op, form, t),
          operation_instructions: form.operation_instructions || null,
          completion_notes: form.completion_notes || null,
          status: form.status || 'PENDING',
          planned_start_date: form.planned_start_date || null,
          planned_end_date: form.planned_end_date || null,
          real_start_date: form.real_start_date || null,
          real_end_date: form.real_end_date || null,
          assigned_user_id: form.assigned_user_id
            ? Number(form.assigned_user_id)
            : null,
        };
      });

      await lineOperationsAPI.saveArticle(orderPublicId, matrixLineNo, {
        operations,
        order_date: orderDate,
        cycle_no: currentCycleNo || 1,
      });
    }

    // Refresh from DB
    const refreshed = await lineOperationsAPI.getForArticle(orderPublicId, matrixLineNo, currentCycleNo);

    // Upload pending images — use savedItem id for existing rows,
    // or find from refresh response for new rows
    if (updatedForm.pendingImages?.length) {
      const opsIdForUpload = savedItem?.line_operations_id ||
        refreshed?.data?.data?.find(s => String(s.operation_id) === key)?.line_operations_id;
      if (opsIdForUpload) {
        try {
          await attachmentsAPI.upload('line_operation', opsIdForUpload, updatedForm.pendingImages);
        } catch (uploadErr) {
          console.error('Attachment upload failed:', uploadErr);
        }
      }
    }

    if (refreshed?.data?.success) {
      const refreshedSaved = refreshed.data.data || [];
      setSavedOps(refreshedSaved);

      const refreshedMap = {};
      refreshedSaved.forEach((s) => {
        refreshedMap[String(s.operation_id)] = s;
      });

      setOpForms((prev) => {
        const next = { ...prev, [key]: updatedForm };

        allOps.forEach((op) => {
          const opKey = String(op.area_operations_id);
          const old = next[opKey] || getEmptyForm();
          const remapped = mapSavedToForm(op, refreshedMap[opKey], t);

          next[opKey] = {
            ...old,
            ...remapped,
            // Clear pending after upload; keep empty array for future uploads
            pendingImages: opKey === key ? [] : (old.pendingImages || []),
          };
        });

        return next;
      });
    } else {
      setOpForms((prev) => ({
        ...prev,
        [key]: updatedForm,
      }));
    }

    setChecked((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    setEditingKey(null);
    toast_success('Detalhes guardados');
  } catch (err) {
    console.error('handleDetailsSave error:', err);
    toast_error(err.response?.data?.error || 'Erro ao guardar detalhes');
  }
};





  const handleNewCycle = async () => {
    if (!window.confirm('Iniciar um novo ciclo? As tarefas em curso serão marcadas como Cancelado.')) return;
    setStartingCycle(true);
    try {
      const res = await lineOperationsAPI.startNewCycle(orderPublicId, matrixLineNo);
      if (res?.data?.success) {
        const { nextCycle } = res.data.data;
        setChecked(new Set());
        setOpForms({});
        setSavedOps([]);
        setActiveFilter('ALL');
        setCurrentCycleNo(nextCycle);
        toast_success(`Ciclo ${nextCycle} iniciado`);
      }
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao iniciar novo ciclo');
    } finally {
      setStartingCycle(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);

    try {
      const operations = allOps.map((op) => {
        const key = String(op.area_operations_id);
        const form = opForms[key] || getEmptyForm();

        return {
          operation_id: op.area_operations_id,
          checked: checked.has(key),
          lead_time_days: op.default_lead_time_days ?? 0,
          result_value: getResultValueForSave(op, form, t),
          operation_instructions: form.operation_instructions || null,
          completion_notes: form.completion_notes || null,
          status: form.status || 'PENDING',

          planned_start_date: form.planned_start_date || null,
          planned_end_date: form.planned_end_date || null,
          real_start_date: form.real_start_date || null,
          real_end_date: form.real_end_date || null,
          assigned_user_id: form.assigned_user_id ? Number(form.assigned_user_id) : null,
        };
      });

      const response = await lineOperationsAPI.saveArticle(orderPublicId, matrixLineNo, {
        operations,
        order_date: orderDate,
        cycle_no: currentCycleNo || 1,
      });

      if (response?.data?.success) {
        const updated = await lineOperationsAPI.getForArticle(orderPublicId, matrixLineNo, currentCycleNo);

        if (updated?.data?.success) {
          const updatedSaved = updated.data.data || [];
          setSavedOps(updatedSaved);

          const updatedSavedMap = {};
          updatedSaved.forEach((s) => {
            updatedSavedMap[String(s.operation_id)] = s;
          });

          setOpForms((prev) => {
            const next = { ...prev };

            allOps.forEach((op) => {
              const key = String(op.area_operations_id);
              const old = next[key] || getEmptyForm();
              const remapped = mapSavedToForm(op, updatedSavedMap[key], t);

              next[key] = {
                ...old,
                ...remapped,
                pendingImages: old.pendingImages || [],
              };
            });

            return next;
          });
        }

        toast_success(t('messages.saved', 'Guardado'));
      }
    } catch (err) {
      console.error(err);
      toast_error(err.response?.data?.error || t('messages.error_save', 'Erro ao guardar'));
    } finally {
      setSaving(false);
    }
  };

  const editingOperation = useMemo(() => {
    if (!editingKey) return null;
    return allOps.find((op) => String(op.area_operations_id) === String(editingKey)) || null;
  }, [editingKey, allOps]);

  const doneCount = savedOps.filter((o) => (
    o.status === 'DONE' || o.status === 'COMPLETED' || o.status === 'N_A'
  )).length;

  const total = savedOps.length;
  const progress = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  const newChecked = [...checked].filter((k) => !savedMap[k]).length;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="lom-modal lom-modal-xl" onClick={(e) => e.stopPropagation()}>
          <div className="lom-header">
            <div className="lom-header-left">
              <h3 className="lom-title">Tarefas</h3>

              {/* Article identity — same pills as LineOperationDetailsModal */}
              <div className="lom-article-subline">
                {(() => {
                  const code     = lineArticle?.enteredProductCode || lineArticle?.erpProductCode || lineLabel;
                  const color    = lineArticle?.enteredProductColor;
                  const erp      = lineArticle?.erpProductCode;
                  const erpColor = lineArticle?.erpProductColorCode;
                  const erpDiffers = erp && erp !== lineArticle?.enteredProductCode;
                  return (
                    <>
                      {code && (
                        <span className="lom-article-pill lom-article-pill--product">
                          {code}
                          {color && <span className="lom-article-color"> · {color}</span>}
                        </span>
                      )}
                      {erpDiffers && (
                        <span className="lom-article-pill lom-article-pill--erp">
                          {erp}{erpColor ? ` / ${erpColor}` : ''}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>

              {total > 0 && (
                <div className="lom-progress-wrap">
                  <div className="lom-progress-bar">
                    <div className="lom-progress-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="lom-progress-label">
                    {doneCount}/{total} {t('areas.completed', 'concluídas')}
                  </span>
                </div>
              )}
            </div>

            {/* Cycle controls: select + new-cycle button */}
            <div className="lom-cycle-controls">
              {availableCycles.length > 0 && (
                <select
                  className="lom-cycle-select"
                  value={currentCycleNo ?? ''}
                  onChange={e => {
                    setChecked(new Set());
                    setOpForms({});
                    setSavedOps([]);
                    setCurrentCycleNo(parseInt(e.target.value, 10));
                  }}
                >
                  {availableCycles.map(cy => (
                    <option key={cy.cycle_no} value={cy.cycle_no}>
                      Ciclo {cy.cycle_no} ({cy.done_ops}/{cy.total_ops})
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                className="lom-new-cycle-btn"
                onClick={handleNewCycle}
                disabled={startingCycle || loading}
                title="Iniciar novo ciclo (cancela tarefas em aberto)"
              >
                <FiRefreshCw size={13} />
                {startingCycle ? 'A criar...' : 'Novo Ciclo'}
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* All comments of the sample order (incl. those made on tasks) */}
              <CommentsButton
                entityType="sample_order_header"
                entityId={orderId}
                disabledTitle="Pedido sem identificador interno"
              />
              {orderId && (
                <button
                  type="button"
                  className="comments-btn"
                  title="Ver histórico de alterações do pedido"
                  onClick={() => setAuditLogOp({
                    entityType: 'sample_order_header',
                    id: orderId,
                    label: lineLabel || 'Pedido',
                  })}
                >
                  <FiClock size={16} />
                </button>
              )}
              <button type="button" className="lom-close" onClick={onClose}>
                <FiX size={18} />
              </button>
            </div>
          </div>

          <div className="lom-toolbar">
            <div className="lom-toolbar-top">
              <div className="lom-toolbar-title">
                <FiFilter size={14} /> FILTRAR
              </div>

              <div className="lom-filter-group">
                {[
                  { key: FILTERS.ALL,          label: 'Todas' },
                  { key: FILTERS.SELECTED,     label: 'Selecionadas' },
                  { key: FILTERS.NOT_SELECTED, label: 'Não Selecionadas' },
                  { key: FILTERS.COMPLETED,    label: 'Concluídas' },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`lom-filter-btn ${activeFilter === item.key ? 'active' : ''}`}
                    onClick={() => setActiveFilter(item.key)}
                  >
                    {item.label} <span>{filterCounts[item.key]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="lom-toolbar-controls">
              <input
                className="lom-search"
                type="text"
                placeholder="Pesquisar operação..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <select
                className="lom-area-select"
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
              >
                <option value="ALL">Todas as áreas</option>
                {areaOptions.map((area) => (
                  <option key={area.area_code} value={area.area_code}>
                    {area.area_code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="lom-body">
            {loading ? (
              <div className="lom-loading">A carregar…</div>
            ) : filteredOps.length === 0 ? (
              <div className="lom-empty">Sem operações para mostrar.</div>
            ) : (
              <div className="lom-table-wrap">
                <div className="lom-table">
                  <div className="lom-table-head">
                    <div className="lom-col-check">Sel.</div>
                    <div className="lom-col-area">Área</div>
                    <div className="lom-col-op">Operação</div>
                    <div className="lom-col-summary">Resumo</div>
                    <div className="lom-col-status">Estado</div>
                    <div className="lom-col-actions">Editar</div>
                  </div>

                  <div className="lom-table-body">
                    {filteredOps.map((op) => {
                      const key = String(op.area_operations_id);
                      const saved = savedMap[key];
                      const form = opForms[key] || getEmptyForm();
                      const summary = getSummary(op, form, !!saved, t);
                      const statusMeta = STATUS_META[form.status || saved?.status || 'PENDING'] || STATUS_META.PENDING;

                      return (
                        <div className="lom-row" key={key}
                          style={op._inactive ? { opacity: 0.55 } : undefined}>
<div className="lom-col-check">
  <input
    type="checkbox"
    className="lom-native-checkbox"
    checked={checked.has(key)}
    disabled={op._inactive && !checked.has(key)}
    onChange={(e) => {
      e.stopPropagation();
      toggleChecked(key);
    }}
    onClick={(e) => e.stopPropagation()}
    aria-label={checked.has(key) ? 'Desmarcar' : 'Marcar'}
  />
</div>

                          <div className="lom-col-area">
                            <span className="lom-area-badge">{op.area_code}</span>
                          </div>

                          <div className="lom-col-op">
                            <button
                              type="button"
                              className="lom-row-link"
                              onClick={() => openDetails(key)}
                            >
                              {op.description || op.operation_label}
                              {op._inactive && (
                                <span style={{ fontSize: 10, fontWeight: 700,
                                  color: '#9ca3af', background: '#f3f4f6',
                                  padding: '1px 5px', borderRadius: 4, marginLeft: 5,
                                  textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                  inativo
                                </span>
                              )}
                            </button>
                          </div>

                          <div className="lom-col-summary">
                            <span className="lom-summary-text">{summary}</span>
                            {(saved?.assigned_user_name || form.assigned_user_id) && (
                              <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
                                <span style={{ opacity: 0.7 }}>👤</span>
                                {saved?.assigned_user_name ||
                                  assignableUsers?.find(u => String(u.user_id) === String(form.assigned_user_id))?.full_name ||
                                  '(responsável definido)'}
                              </div>
                            )}
                          </div>

                          <div className="lom-col-status">
                            <span className={`status-badge ${statusMeta.cls}`}>
                              {statusMeta.label}
                            </span>
                          </div>

                          <div className="lom-col-actions">
                            <button
                              type="button"
                              className="lom-edit-btn"
                              title="Editar"
                              onClick={() => openDetails(key)}
                            >
                              <FiEdit2 size={14} />
                            </button>
                            {form.line_operations_id && (
                              <button
                                type="button"
                                className="lom-edit-btn"
                                title="Ver histórico de alterações"
                                onClick={() => setAuditLogOp({
                                  entityType: 'line_operation',
                                  id: form.line_operations_id,
                                  label: op.description || op.operation_label,
                                })}
                              >
                                <FiClock size={14} />
                              </button>
                            )}

                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lom-footer">
            <div className="lom-footer-actions">
              <button type="button" className="btn-secondary btn-sm" onClick={onClose}>
                {t('cancel', 'Cancelar')}
              </button>

            </div>
          </div>

          <div className="toast-container">
            {toasts.map((toast) => (
              <Toast key={toast.id} {...toast} onClose={removeToast} />
            ))}
          </div>
        </div>
      </div>

      {editingOperation && (
        <LineOperationDetailsModal
          op={editingOperation}
          form={opForms[String(editingOperation.area_operations_id)] || getEmptyForm()}
          assignableUsers={assignableUsers}
          lineArticle={lineArticle}
          lineLabel={lineLabel}
          onClose={closeDetails}
          orderId={orderId}
          commentLabel={taskCommentLabel(editingOperation)}
          onSave={(updatedForm) =>
            handleDetailsSave(String(editingOperation.area_operations_id), updatedForm)
          }
        />
      )}

      {auditLogOp && (
        <AuditLogPanel
          isOpen={!!auditLogOp}
          onClose={() => setAuditLogOp(null)}
          entityType={auditLogOp.entityType || 'line_operation'}
          entityId={auditLogOp.id?.toString()}
          title={`${t('audit.title', 'Histórico de Alterações')} - ${auditLogOp.label}`}
        />
      )}
    </>
  );
};

export default LineOperationsModal;